// gridLayout.js — PURE layout helpers for the ImageGrid CSS-Grid render path.
// No React, no DOM, no MUI: just deterministic math so the grid comes out
// flush/balanced and the helpers are unit-testable in isolation.

// ---------------------------------------------------------------------------
// Aspect-ratio estimation
// ---------------------------------------------------------------------------
// We never have intrinsic pixel dimensions at layout time (images load lazily),
// so we estimate each image's *relative height* from whatever ratio metadata it
// carries, falling back to a square (1) when nothing is known. The estimate is
// only used to balance columns — it never crops or distorts the rendered image.
//
// G8 NOTE: masonry balance is only as good as this aspect metadata. Images that
// arrive without width/height/aspectRatio/ratio are treated as squares (1), so a
// list of metadata-less images degrades to an even left-to-right fill rather than
// a true height-balanced layout. Supplying real dims (e.g. naturalWidth/Height
// read post-load) tightens the balance; no behaviour change is required here.

/**
 * Best-effort relative height for an image, assuming a unit column width of 1.
 * height = 1 / aspectRatio, where aspectRatio = width / height.
 * Unknown / invalid (including Infinity / -Infinity / NaN / <= 0) -> 1 (treated
 * as a square). Always returns a finite number > 0 so masonry math can't break.
 * @param {object} img
 * @returns {number}
 */
export const estimateAspectHeight = (img) => {
  if (!img || typeof img !== "object") return 1;

  // The inputs are finite+positive, but the *quotient* can still overflow to
  // Infinity (e.g. width 1e-300, height 1e308) or underflow to 0 — so clamp the
  // RESULT, not just the inputs, to a finite > 0 value. (Found by a fast-check
  // fuzz property: estimateAspectHeight must always be finite > 0.)
  const finite = (v) => (isFinite(v) && v > 0 ? v : 1);

  // 1. Explicit numeric width & height (intrinsic dims if ever present).
  const w = Number(img.width);
  const h = Number(img.height);
  if (isFinite(w) && isFinite(h) && w > 0 && h > 0) {
    return finite(h / w);
  }

  // 2. An explicit aspectRatio (width/height) field, if provided.
  const ar = Number(img.aspectRatio);
  if (isFinite(ar) && ar > 0) {
    return finite(1 / ar);
  }

  // 3. A "W:H" / "W/H" ratio string (e.g. "16:9").
  if (typeof img.ratio === "string") {
    const m = img.ratio.match(/^\s*(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
    if (m) {
      const rw = Number(m[1]);
      const rh = Number(m[2]);
      if (rw > 0 && rh > 0) return finite(rh / rw);
    }
  }

  // 4. Unknown -> square.
  return 1;
};

// ---------------------------------------------------------------------------
// Balanced masonry
// ---------------------------------------------------------------------------
/**
 * Distribute images into `columns` columns by greedy shortest-column placement
 * so the column bottoms come out near-even (fixes the ragged-bottom complaint).
 *
 * Guarantees:
 *  - every input image lands in exactly one column (no drops, no dupes);
 *  - original input order is preserved *within* each column;
 *  - returns exactly `columns` arrays (some may be empty when images < columns);
 *  - falls back to round-robin-equivalent behaviour when ratios are unknown
 *    (all heights == 1 -> greedy fills columns left-to-right, evenly).
 *
 * @param {Array<object>} images
 * @param {number} columns
 * @returns {Array<Array<object>>} columns of images, length === columns
 */
export const balanceMasonryColumns = (images, columns) => {
  const colCount = Math.max(1, Math.floor(Number(columns) || 1));
  const cols = Array.from({ length: colCount }, () => []);
  const heights = new Array(colCount).fill(0);

  if (!Array.isArray(images) || images.length === 0) return cols;

  for (const img of images) {
    // Pick the currently-shortest column; ties -> lowest index (stable, keeps
    // left-to-right fill order for equal-height images). Use a *relative*
    // epsilon (G1): an absolute 1e-9 is swamped by accumulated float error at
    // larger mixed-ratio heights, which let columns pack unevenly. Scaling the
    // tolerance to the running column height keeps the tie-break stable while
    // still treating genuinely-shorter columns as shorter.
    let target = 0;
    for (let c = 1; c < colCount; c++) {
      const eps = Math.max(1e-5, heights[target] * 1e-6);
      if (heights[c] < heights[target] - eps) target = c;
    }
    cols[target].push(img);
    heights[target] += estimateAspectHeight(img);
  }

  return cols;
};

// ---------------------------------------------------------------------------
// Deterministic quilted pattern
// ---------------------------------------------------------------------------
// The original quilted look came from utils/helpers `predefinedLayouts`, cycled
// by index. We keep that EXACT cycle (index-based, fully deterministic — no
// randomness) but expose it as a pure span-pattern the v2 CSS-grid renderer can
// consume. Identical spans for a given list length => stable across renders.
export const QUILTED_PATTERN = [
  { cols: 2, rows: 2 },
  { cols: 1, rows: 1 },
  { cols: 1, rows: 1 },
  { cols: 1, rows: 1 },
  { cols: 2, rows: 2 },
  { cols: 1, rows: 1 },
  { cols: 2, rows: 1 },
  { cols: 1, rows: 1 },
  { cols: 1, rows: 1 },
];

/**
 * The {cols, rows} CSS-grid span for the image at `index` in a quilted grid.
 * Pure index -> pattern lookup (cycles QUILTED_PATTERN). Deterministic.
 * @param {number} index
 * @returns {{cols: number, rows: number}}
 */
export const quiltedSpanAt = (index) => {
  const i = Math.abs(Math.floor(Number(index) || 0));
  return QUILTED_PATTERN[i % QUILTED_PATTERN.length];
};

/**
 * Map a list of images to their deterministic quilted spans, preserving order
 * and never dropping/duplicating. Returns a NEW array (does not mutate input).
 * @param {Array<object>} images
 * @returns {Array<object>} images each merged with {cols, rows}
 */
export const applyQuiltedPattern = (images) => {
  if (!Array.isArray(images)) return [];
  return images.map((img, i) => ({ ...img, ...quiltedSpanAt(i) }));
};

// ---------------------------------------------------------------------------
// CDN URL — preserve-ratio, NO forced crop
// ---------------------------------------------------------------------------
/**
 * Build a width-constrained, preserve-ratio CDN URL + dpr-2 srcSet for v2.
 * Unlike the old srcset() in helpers.js this requests ONLY a width (`w`) plus
 * `auto=format` — it never sends `h=` or `fit=crop`, so the CDN returns the
 * image at its natural aspect ratio (object-fit: contain handles the cell).
 *
 * @param {string} src    base image URL (may be a data/blob URL or already have a query)
 * @param {number} width  target CSS width of the cell in px (defaults 248)
 * @returns {{src: string, srcSet: string}}
 */
export const buildContainSrc = (src, width = 248) => {
  if (typeof src !== "string" || src.length === 0) {
    return { src: src || "", srcSet: undefined };
  }
  // Don't append query params to data:/blob: URLs (uploads-in-progress) or if
  // the URL already carries a query (respect what's there, don't double-crop).
  const isLocal = src.startsWith("data:") || src.startsWith("blob:");
  const hasQuery = src.includes("?");
  if (isLocal || hasQuery) {
    return { src, srcSet: undefined };
  }
  // G9: guard non-finite / garbage widths. NaN/Infinity/-Infinity (and the 0/""
  // falsy cases) fall back to the 248 default; a finite negative width clamps to
  // the 1px minimum, and an absurdly-large finite width clamps to MAX_CDN_W so the
  // dpr-2 srcSet (`w * 2`) can't overflow toward Infinity. (Upper clamp found by a
  // fast-check fuzz property — extreme finite widths near Number.MAX_VALUE.)
  const MAX_CDN_W = 8192;
  const reqW = Number(width);
  const safeW = isFinite(reqW) ? reqW || 248 : 248;
  const w = Math.max(1, Math.min(MAX_CDN_W, Math.round(safeW)));
  const base = `${src}?w=${w}&auto=format`;
  return {
    src: base,
    srcSet: `${src}?w=${w * 2}&auto=format&dpr=2 2x`,
  };
};
